import { PropsWithChildren, useCallback, useRef } from 'react';
import { useDrag, useDrop } from 'react-dnd';
import cn from 'classnames';
import './DraggableRow.scss';

interface DragItem {
  type: 'ROW';
  index: number;
}

interface DraggableRowProps {
  index: number;
  moveRow: (dragIndex: number, hoverIndex: number) => void;
  onDragEnd?: () => void;
  isDragSource?: boolean;
}

export const DraggableRow = ({
  index,
  moveRow,
  onDragEnd = () => {},
  isDragSource = true,
  children
}: PropsWithChildren<DraggableRowProps>) => {
  const nodeRef = useRef<HTMLSpanElement | null>(null);

  const [{ isActiveDragRow }, drop] = useDrop<DragItem, void, { isActiveDragRow: boolean }>({
    accept: 'ROW',
    collect: monitor => ({
      isActiveDragRow: monitor.getItem()?.index === index
    }),
    hover(item, monitor) {
      const node = nodeRef.current;
      if (!node) {
        return;
      }

      const dragIndex = item.index;
      const hoverIndex = index;

      if (dragIndex === hoverIndex) {
        return;
      }

      // firstElementChild is the cell div, whose box spans the full row height
      // (each cell is absolutely positioned with height = rowHeight). The midpoint
      // check only uses the vertical axis, so measuring one cell == measuring the row.
      const rowEl = node.firstElementChild ?? node;
      const rect = rowEl.getBoundingClientRect();
      const clientOffset = monitor.getClientOffset();
      if (!clientOffset) {
        return;
      }
      const hoverMiddleY = (rect.bottom - rect.top) / 2;
      const hoverClientY = clientOffset.y - rect.top;
      const draggingDownward = dragIndex < hoverIndex;
      if (draggingDownward && hoverClientY < hoverMiddleY) {
        return;
      }
      if (!draggingDownward && hoverClientY > hoverMiddleY) {
        return;
      }

      moveRow(dragIndex, hoverIndex);
      Object.assign(item, { index: hoverIndex });
    }
  });

  const [, drag] = useDrag<DragItem>({
    type: 'ROW',
    item: { type: 'ROW', index },
    end: () => onDragEnd()
  });

  const dragDropRef = useCallback(
    (node: HTMLSpanElement | null) => {
      nodeRef.current = node;
      drop(node);
      if (isDragSource) {
        drag(node);
      }
    },
    [drag, drop, isDragSource]
  );

  const className = cn('bpm-table-draggable-row', {
    'is-dragging': isActiveDragRow,
    'is-drag-source': isDragSource
  });

  return (
    <span ref={dragDropRef} className={className}>
      {children}
    </span>
  );
};
